/**
 * LaTeX math rendering for LLM output, as a `marked` extension backed by
 * KaTeX. Handles the delimiters our models actually emit: `\[…\]` and
 * `$$…$$` (display) and `\(…\)` (inline). Bare `$…$` is deliberately NOT
 * matched — it collides with currency in prose ("$5 to $10").
 *
 * The render→substitute split exists because of the badge post-processor.
 * `wrapNumericBadges` runs on the parsed HTML *before* DOMPurify and injects
 * `<span class="badge-solid">` around numbers; KaTeX output is dense nested
 * spans full of digits, so badge-wrapping would shatter the equation markup.
 * So the extension renders KaTeX into a module-level buffer and emits an
 * inert empty placeholder span; MarkdownView calls `substituteMath` only
 * AFTER `wrapNumericBadges`, swapping placeholders for the real KaTeX HTML.
 *
 * The module buffer is safe despite being shared: `marked.parse` is fully
 * synchronous, so a MarkdownView memo runs reset → parse → substitute as one
 * uninterrupted block — no other parse can interleave (JS is single-threaded
 * and there is no await between the steps).
 */
import katex from "katex";
import type { TokenizerAndRendererExtension, Tokens } from "marked";

let buffer: string[] = [];

const PLACEHOLDER_RE = /<span class="z01-math-ph" data-i="(\d+)"><\/span>/g;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Render LaTeX → KaTeX HTML, stash it, return an inert placeholder. With
// `throwOnError: false` KaTeX renders malformed input as a visible error
// span instead of throwing, so a single bad equation can't blank the turn.
function renderMath(latex: string, displayMode: boolean): string {
  let html: string;
  try {
    html = katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      strict: "ignore",
      output: "htmlAndMathml",
    });
  } catch {
    // Defensive: renderToString shouldn't throw under throwOnError:false,
    // but if it ever does, degrade to the readable literal source.
    const delims = displayMode ? ["\\[ ", " \\]"] : ["\\( ", " \\)"];
    html = escapeHtml(`${delims[0]}${latex}${delims[1]}`);
  }
  const idx = buffer.push(html) - 1;
  return `<span class="z01-math-ph" data-i="${idx}"></span>`;
}

const blockMath: TokenizerAndRendererExtension = {
  name: "blockMath",
  level: "block",
  start(src) {
    return src.match(/\\\[|\$\$/)?.index;
  },
  tokenizer(src) {
    // Both forms require the CLOSING delimiter — incomplete math during
    // streaming stays literal text and snaps to rendered once it arrives.
    const bracket = /^\\\[([\s\S]+?)\\\]/.exec(src);
    const dollars = bracket ? null : /^\$\$([\s\S]+?)\$\$/.exec(src);
    const m = bracket ?? dollars;
    if (!m) return undefined;
    return { type: "blockMath", raw: m[0], text: (m[1] ?? "").trim() };
  },
  renderer(token) {
    return renderMath((token as Tokens.Generic & { text: string }).text, true);
  },
};

const inlineMath: TokenizerAndRendererExtension = {
  name: "inlineMath",
  level: "inline",
  start(src) {
    return src.match(/\\\(/)?.index;
  },
  tokenizer(src) {
    const m = /^\\\(([\s\S]+?)\\\)/.exec(src);
    if (!m) return undefined;
    return { type: "inlineMath", raw: m[0], text: (m[1] ?? "").trim() };
  },
  renderer(token) {
    return renderMath((token as Tokens.Generic & { text: string }).text, false);
  },
};

export const mathExtension = { extensions: [blockMath, inlineMath] };

// Reset the render buffer. Call once before each `marked.parse`.
export function resetMath(): void {
  buffer = [];
}

// Swap rendered-math placeholders for their KaTeX HTML. Call AFTER
// wrapNumericBadges and BEFORE DOMPurify.
export function substituteMath(html: string): string {
  return html.replace(
    PLACEHOLDER_RE,
    (_, i: string) => buffer[Number(i)] ?? "",
  );
}
